import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { StorageSerializers, useLocalStorage } from "@vueuse/core";

import { User, UserPermission, UserRole } from "@/shared/types/user";
import type { AuthData } from "@/shared/types/auth";
import { LOCAL_STORAGE_KEY } from "@/constants";
import { Device } from "@/shared/types/device";

export const useAuthStore = defineStore("auth", () => {
    /*#############
    ### 1. USER ###
    #############*/
    const user = ref<User | null>(null);

    const last_logged_in_user_role = useLocalStorage<UserRole | null>(
        LOCAL_STORAGE_KEY.AUTH_LAST_LOGGED_IN_USER_ROLE,
        null,
        {
            serializer: StorageSerializers.string,
            listenToStorageChanges: true
        }
    );

    function setUser(u: User) {
        user.value = u;
        last_logged_in_user_role.value = u.role;
    }

    function unsetUser() {
        user.value = null;
    }

    function getUserInitials(subject_user = user.value): string {
        if (!subject_user) return "?";
        return `${subject_user.first_name?.[0] || ""}${subject_user.last_name?.[0] || ""}`;
    }

    function getUserFullName(subject_user = user.value): string {
        if (!subject_user) return "n/a";
        return `${subject_user.first_name} ${subject_user.last_name}`.trim();
    }
    /*##################
    ### 2. AUTH DATA ###
    ##################*/
    const auth_data = useLocalStorage<AuthData | null>(LOCAL_STORAGE_KEY.AUTH_DATA, null, {
        serializer: StorageSerializers.object,
        listenToStorageChanges: true
    });

    const is_logged_in = computed(() => !!auth_data.value);

    function setAuthData(ad: AuthData) {
        auth_data.value = {
            access_token: ad.access_token,
            access_token_exp_date: ad.access_token_exp_date,
            refresh_token: ad.refresh_token,
            refresh_token_exp_date: ad.refresh_token_exp_date
        };
    }

    function unsetAuthData() {
        auth_data.value = null;
    }
    /*####################
    ### 3. USER DEVICE ###
    ####################*/
    const user_device = ref<Device | null>(null);

    function setUserDevice(device: Device) {
        user_device.value = device;
    }

    function unsetUserDevice() {
        user_device.value = null;
    }
    /*#########################
    ### 4. USER PERMISSIONS ###
    #########################*/
    const user_permissions = ref<UserPermission[]>([]);

    function userHasPermissions(permissions: UserPermission | UserPermission[]) {
        if (!Array.isArray(permissions)) permissions = [permissions];
        let has_all = true;
        for (let i = 0; i < permissions.length; i++) {
            const ix = user_permissions.value.indexOf(permissions[i]);
            if (ix === -1) {
                has_all = false;
                break;
            }
        }
        return has_all;
    }

    function setUserPermissions(permissions: UserPermission | UserPermission[]) {
        if (!Array.isArray(permissions)) permissions = [permissions];
        user_permissions.value = permissions;
    }

    function unsetUserPermissions() {
        user_permissions.value = [];
    }

    /*################
    ### 5. GENERAL ###
    ################*/
    function unsetAuthState() {
        unsetAuthData();
        unsetUser();
        unsetUserDevice();
        unsetUserPermissions();
    }

    return {
        user,
        auth_data,
        user_device,
        is_logged_in,
        user_permissions,
        last_logged_in_user_role,

        getUserInitials,
        getUserFullName,

        setUser,
        unsetUser,

        setAuthData,
        unsetAuthData,

        setUserDevice,
        unsetUserDevice,

        userHasPermissions,
        setUserPermissions,
        unsetUserPermissions,
        unsetAuthState
    };
});
